import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, MousePointerClick, Users, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import LinkManager from '../components/LinkManager';
import ContactManager from '../components/ContactManager';
import LineChart from '../components/LineChart';
import TimeRangeSelector from '../components/TimeRangeSelector';
import DataModal from '../components/DataModal';
import '../styles/admin.css';

const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [activityPage, setActivityPage] = useState(1);
  const [activityPerPage, setActivityPerPage] = useState(20);
  const [selectedClicksLink, setSelectedClicksLink] = useState('all');
  const [selectedLeadsLink, setSelectedLeadsLink] = useState('all');
  const [timeRange, setTimeRange] = useState(7); // Days
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check if we have a token
      const token = localStorage.getItem('wne_admin_token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Verify token with backend
      const authResponse = await api.verifyAuth();
      setAdminEmail(authResponse.email);
      
      // Load data
      await loadData();
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/login');
    }
  };

  const loadData = async () => {
    try {
      const [analyticsData, leadsData, configData] = await Promise.all([
        api.getAnalytics(timeRange),
        api.getLeads(),
        api.getConfig()
      ]);
      setStats(analyticsData);
      setLeads(leadsData);
      setConfig(configData);
    } catch (error) {
      console.error('Failed to load data:', error);
      // If we get 401, redirect to login
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Reload data when time range changes
  useEffect(() => {
    if (!loading) {
      loadData();
    }
  }, [timeRange]);

  const handleLogout = () => {
    api.logout();
    navigate('/login');
  };

  // Filter clicks data by selected link
  const getFilteredClicksData = () => {
    if (!stats?.clicks_over_time) return {};
    
    if (selectedClicksLink === 'all') {
      return stats.clicks_over_time;
    }

    // Filter events by selected link and recalculate time-series
    const filteredData = {};
    Object.keys(stats.clicks_over_time).forEach(date => {
      filteredData[date] = 0;
    });

    // Count clicks for selected link per day from recent_events
    if (stats.recent_events) {
      stats.recent_events.forEach(event => {
        if (event.event === 'link_click' && event.label === selectedClicksLink) {
          const eventDate = new Date(event.timestamp).toISOString().split('T')[0];
          if (filteredData.hasOwnProperty(eventDate)) {
            filteredData[eventDate]++;
          }
        }
      });
    }

    return filteredData;
  };

  // Filter leads data by selected link (resource)
  const getFilteredLeadsData = () => {
    if (!stats?.leads_over_time) return {};
    
    if (selectedLeadsLink === 'all') {
      return stats.leads_over_time;
    }

    // Filter leads by selected resource and recalculate time-series
    const filteredData = {};
    Object.keys(stats.leads_over_time).forEach(date => {
      filteredData[date] = 0;
    });

    // Count leads for selected resource per day
    if (leads?.leads) {
      leads.leads.forEach(lead => {
        if (lead.resource === selectedLeadsLink) {
          const leadDate = new Date(lead.timestamp).toISOString().split('T')[0];
          if (filteredData.hasOwnProperty(leadDate)) {
            filteredData[leadDate]++;
          }
        }
      });
    }

    return filteredData;
  };

  const downloadLeadsCSV = () => {
    if (!leads?.leads || leads.leads.length === 0) {
      return;
    }

    // Prepare CSV headers
    const headers = ['Name', 'Email', 'Phone', 'LinkedIn', 'Resource', 'Date/Time'];
    
    // Prepare CSV rows
    const rows = leads.leads.map(lead => [
      lead.name || '',
      lead.email || '',
      lead.phone || '',
      lead.linkedin || '',
      lead.resource,
      new Date(lead.timestamp).toLocaleString()
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `report-access-leads-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLinkUpdate = async (action, linkId, data) => {
    try {
      setUpdating(true);
      switch (action) {
        case 'create':
          await api.createLink(data);
          break;
        case 'update':
          await api.updateLink(linkId, data);
          break;
        case 'delete':
          await api.deleteLink(linkId);
          break;
        case 'reorder':
          // data is the array of link IDs in new order
          await api.reorderLinks(data);
          break;
        default:
          break;
      }
      // Reload all data to ensure UI is in sync
      await loadData();
    } catch (error) {
      console.error('Failed to update link:', error);
      alert('Failed to update link. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleContactUpdate = async (action, contactId, data) => {
    try {
      setUpdating(true);
      switch (action) {
        case 'create':
          await api.createContact(data);
          break;
        case 'update':
          await api.updateContact(contactId, data);
          break;
        case 'delete':
          await api.deleteContact(contactId);
          break;
        case 'reorder':
          // data is the array of contact IDs in new order
          await api.reorderContacts(data);
          break;
        default:
          break;
      }
      // Reload all data to ensure UI is in sync
      await loadData();
    } catch (error) {
      console.error('Failed to update contact:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Better error message based on status code
      if (error.response?.status === 401 || error.response?.status === 403) {
        alert('Authentication expired. Please log in again.');
        navigate('/login');
      } else if (error.response?.data?.detail) {
        alert(`Failed to update contact: ${error.response.data.detail}`);
      } else {
        alert('Failed to update contact. Please try again.');
      }
    } finally {
      setUpdating(false);
    }
  };

  // Handle chart point click
  const handleChartPointClick = (dateKey, value, index) => {
    // Get events for this specific date
    const clickedDate = new Date(dateKey);
    const eventsForDate = stats?.recent_events?.filter(event => {
      const eventDate = new Date(event.timestamp);
      return eventDate.toDateString() === clickedDate.toDateString();
    }) || [];

    // Count clicks and leads for this date
    const clickEvents = eventsForDate.filter(e => e.event === 'link_click');
    const leadEvents = eventsForDate.filter(e => e.event === 'lead_captured');

    setModalTitle(clickedDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }));
    
    setModalData({
      events: eventsForDate,
      clicks: clickEvents.length,
      leads: leadEvents.length
    });
    
    setModalOpen(true);
  };

  // Handle bar click for filtering
  const handleBarClick = (linkName) => {
    if (selectedClicksLink === linkName) {
      // If already selected, clear filter
      setSelectedClicksLink('all');
    } else {
      // Set new filter
      setSelectedClicksLink(linkName);
    }
  };


  const handleBrandingUpdate = async () => {
    try {
      setUpdating(true);
      await api.updateConfig({
        title: config.title,
        tagline: config.tagline
      });
      alert('Branding updated successfully!');
      // Reload all data
      await loadData();
    } catch (error) {
      console.error('Failed to update branding:', error);
      alert('Failed to update branding. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-content">
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      {updating && (
        <div className="updating-overlay">
          <div className="updating-message">
            <div className="spinner"></div>
            Updating...
          </div>
        </div>
      )}
      <div className="admin-content">
        <header className="admin-header">
          <div>
            <h1>Analytics Dashboard</h1>
            {adminEmail && <p className="admin-email">Logged in as: {adminEmail}</p>}
          </div>
          <div className="admin-actions">
            <button onClick={loadData} className="refresh-btn" disabled={updating}>
              Refresh Data
            </button>
            <button onClick={handleLogout} className="logout-btn">
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </header>

        {/* Time Range Selector */}
        <TimeRangeSelector 
          value={timeRange} 
          onChange={setTimeRange}
        />

        {/* Stats Overview */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <MousePointerClick size={32} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats?.total_clicks || 0}</div>
              <div className="stat-label">Total Clicks</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Users size={32} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{leads?.total || 0}</div>
              <div className="stat-label">Leads Captured</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <TrendingUp size={32} />
            </div>
            <div className="stat-info">
              <div className="stat-value">
                {Object.keys(stats?.by_label || {}).length}
              </div>
              <div className="stat-label">Active Links</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <BarChart3 size={32} />
            </div>
            <div className="stat-info">
              <div className="stat-value">
                {stats?.recent_events?.length || 0}
              </div>
              <div className="stat-label">Recent Events</div>
            </div>
          </div>
        </div>

        {/* Branding Settings */}
        {config && (
          <div className="section">
            <h2>Page Branding</h2>
            <div className="branding-form">
              <div className="form-group">
                <label htmlFor="page-title">Page Title</label>
                <input
                  id="page-title"
                  type="text"
                  value={config.title || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Congressional Energy Engagement Group"
                  className="branding-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="page-tagline">Page Tagline</label>
                <input
                  id="page-tagline"
                  type="text"
                  value={config.tagline || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, tagline: e.target.value }))}
                  placeholder="Advancing Energy. Strengthening America. Leading the World."
                  className="branding-input"
                />
              </div>
              <button 
                onClick={handleBrandingUpdate} 
                className="save-branding-btn"
              >
                Save Branding
              </button>
            </div>
          </div>
        )}

        {/* Link Manager */}
        {config && config.links && (
          <LinkManager 
            links={config.links} 
            onUpdate={handleLinkUpdate}
          />
        )}

        {/* Contact Manager */}
        {config && (
          <ContactManager 
            contacts={config.contacts || []} 
            onUpdate={handleContactUpdate}
          />
        )}

        {/* Leads Analytics */}
        <div className="section">
          <div className="section-header">
            <h2>Leads Captured Over Time</h2>
            <div className="chart-filter">
              <label htmlFor="leads-filter">Filter by Resource:</label>
              <select 
                id="leads-filter"
                value={selectedLeadsLink}
                onChange={(e) => setSelectedLeadsLink(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Resources</option>
                {config?.links?.filter(link => link.enabled !== false && link.has_gate).map(link => (
                  <option key={link.id} value={link.gate_resource_name || link.title}>
                    {link.gate_resource_name || link.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Time-series Chart */}
          {stats?.leads_over_time && Object.keys(stats.leads_over_time).length > 0 && (
            <LineChart
              data={getFilteredLeadsData()}
              label={selectedLeadsLink === 'all' ? 'Leads' : `Leads - ${selectedLeadsLink}`}
              color="#10b981"
              onPointClick={handleChartPointClick}
            />
          )}
        </div>

        {/* Leads Section */}
        <div className="section">
          <div className="section-header">
            <h2>Gated Access Requests ({leads?.total || 0})</h2>
            {leads?.leads?.length > 0 && (
              <button onClick={downloadLeadsCSV} className="download-csv-btn">
                Download CSV
              </button>
            )}
          </div>
          <div className="leads-table">
            {leads?.leads?.length > 0 ? (
              <>
                {leads.leads
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((lead, idx) => (
                    <div key={idx} className="lead-row">
                      <div className="lead-name">{lead.name || '-'}</div>
                      <div className="lead-email">{lead.email || '-'}</div>
                      {(lead.phone || lead.linkedin) && (
                        <div className="lead-extra-info">
                          {lead.phone && <div className="lead-phone">📱 {lead.phone}</div>}
                          {lead.linkedin && <div className="lead-linkedin">🔗 {lead.linkedin}</div>}
                        </div>
                      )}
                      <div className="lead-resource">{lead.resource}</div>
                      <div className="lead-time">
                        {new Date(lead.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))
                }
              </>
            ) : (
              <p className="no-data">No leads captured yet</p>
            )}
          </div>
          
          {/* Pagination Controls */}
          {leads?.leads?.length > 0 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, leads.total)} of {leads.total} leads
              </div>
              
              <div className="pagination-buttons">
                <button 
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  First
                </button>
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                
                <span className="pagination-page-info">
                  Page {currentPage} of {Math.ceil(leads.total / itemsPerPage)}
                </span>
                
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(leads.total / itemsPerPage), prev + 1))}
                  disabled={currentPage >= Math.ceil(leads.total / itemsPerPage)}
                  className="pagination-btn"
                >
                  Next
                </button>
                <button 
                  onClick={() => setCurrentPage(Math.ceil(leads.total / itemsPerPage))}
                  disabled={currentPage >= Math.ceil(leads.total / itemsPerPage)}
                  className="pagination-btn"
                >
                  Last
                </button>
              </div>
              
              <div className="pagination-size">
                <label htmlFor="items-per-page">Items per page:</label>
                <select 
                  id="items-per-page"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                  className="pagination-select"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Clicks by Label */}
        <div className="section">
          <div className="section-header">
            <h2>Clicks by Link</h2>
            <div className="chart-filter">
              <label htmlFor="clicks-filter">Filter by Link:</label>
              <select 
                id="clicks-filter"
                value={selectedClicksLink}
                onChange={(e) => setSelectedClicksLink(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Links</option>
                {config?.links?.filter(link => link.enabled !== false).map(link => (
                  <option key={link.id} value={link.title}>{link.title}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Time-series Chart */}
          {stats?.clicks_over_time && Object.keys(stats.clicks_over_time).length > 0 && (
            <LineChart
              data={getFilteredClicksData()}
              label={selectedClicksLink === 'all' ? 'Clicks' : `Clicks - ${selectedClicksLink}`}
              color="#3b82f6"
              onPointClick={handleChartPointClick}
            />
          )}
          
          {/* Bar Chart */}
          <div className="label-stats" style={{ marginTop: '2rem' }}>
            {Object.entries(stats?.by_label || {}).map(([label, count]) => {
              const percentage = stats.total_clicks > 0 ? ((count / stats.total_clicks) * 100).toFixed(1) : 0;
              const isSelected = selectedClicksLink === label;
              return (
                <div 
                  key={label} 
                  className={`label-stat-row ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleBarClick(label)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="label-name">
                    {label}
                    {isSelected && <span className="filter-badge">Filtering</span>}
                  </div>
                  <div className="label-bar-container">
                    <div
                      className="label-bar"
                      style={{
                        width: `${(count / stats.total_clicks) * 100}%`
                      }}
                      title={`Click to filter | ${label}: ${count} clicks (${percentage}%)`}
                    >
                      {count >= 3 && `${percentage}%`}
                    </div>
                  </div>
                  <div className="label-count">{count}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Events */}
        <div className="section">
          <h2>Recent Activity ({stats?.recent_events?.length || 0} total)</h2>
          <div className="events-table">
            {stats?.recent_events?.length > 0 ? (
              <>
                {stats.recent_events
                  .slice((activityPage - 1) * activityPerPage, activityPage * activityPerPage)
                  .map((event, idx) => (
                    <div key={idx} className="event-row">
                      <div className="event-type">{event.event}</div>
                      <div className="event-label">{event.label || '-'}</div>
                      <div className="event-time">
                        {new Date(event.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))
                }
              </>
            ) : (
              <p className="no-data">No activity yet</p>
            )}
          </div>

          {/* Activity Pagination Controls */}
          {stats?.recent_events?.length > 0 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                Showing {((activityPage - 1) * activityPerPage) + 1} - {Math.min(activityPage * activityPerPage, stats.recent_events.length)} of {stats.recent_events.length} events
              </div>
              
              <div className="pagination-buttons">
                <button 
                  onClick={() => setActivityPage(1)}
                  disabled={activityPage === 1}
                  className="pagination-btn"
                >
                  First
                </button>
                <button 
                  onClick={() => setActivityPage(prev => Math.max(1, prev - 1))}
                  disabled={activityPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                
                <span className="pagination-page-info">
                  Page {activityPage} of {Math.ceil(stats.recent_events.length / activityPerPage)}
                </span>
                
                <button 
                  onClick={() => setActivityPage(prev => Math.min(Math.ceil(stats.recent_events.length / activityPerPage), prev + 1))}
                  disabled={activityPage >= Math.ceil(stats.recent_events.length / activityPerPage)}
                  className="pagination-btn"
                >
                  Next
                </button>
                <button 
                  onClick={() => setActivityPage(Math.ceil(stats.recent_events.length / activityPerPage))}
                  disabled={activityPage >= Math.ceil(stats.recent_events.length / activityPerPage)}
                  className="pagination-btn"
                >
                  Last
                </button>
              </div>
              
              <div className="pagination-size">
                <label htmlFor="activity-per-page">Items per page:</label>
                <select 
                  id="activity-per-page"
                  value={activityPerPage}
                  onChange={(e) => {
                    setActivityPerPage(Number(e.target.value));
                    setActivityPage(1); // Reset to first page when changing items per page
                  }}
                  className="pagination-select"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Data Modal */}
      <DataModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        data={modalData}
        title={modalTitle}
      />
    </div>
  );
};

export default AdminPage;
